import { cognitoConfig } from "@src/appV2/Auth/cognito/config";

/**
 * Runs the EMAIL_OTP re-auth against Cognito WITHOUT touching the primary Amplify
 * session. The in-app email-OTP flow drives Amplify `signIn`, which signs the current
 * user out on UserAlreadyAuthenticatedException, so a cancelled step-up would strand the
 * workplace user logged out. This module speaks the same wire protocol via plain fetch
 * (InitiateAuth USER_AUTH + PREFERRED_CHALLENGE=EMAIL_OTP, then RespondToAuthChallenge
 * with EMAIL_OTP_CODE) — the minted tokens never enter Amplify storage. The fresh ID
 * token is posted to home-health-api's step-up elevate endpoint; the primary session's
 * tokens remain the ones attached to every API request.
 */

const EMAIL_OTP_CHALLENGE = "EMAIL_OTP";

// A stalled Cognito call must not wedge the step-up modal (Cancel is disabled while a
// send or verify is in flight), so every request carries a hard timeout.
const COGNITO_REQUEST_TIMEOUT_MS = 30_000;

interface CognitoErrorBody {
  __type?: string;
  message?: string;
}

export class CognitoSideChannelError extends Error {
  constructor(
    public readonly type: string,
    message: string
  ) {
    super(message);
    this.name = "CognitoSideChannelError";
  }

  public get isWrongOtp(): boolean {
    return this.type === "CodeMismatchException" || this.type === "ExpiredCodeException";
  }

  public get isExpiredSession(): boolean {
    return this.type === "NotAuthorizedException" || this.type === "ExpiredSessionException";
  }

  /** Cognito throttles per-user auth attempts; retrying immediately only extends the lockout. */
  public get isRateLimited(): boolean {
    return this.type === "LimitExceededException" || this.type === "TooManyRequestsException";
  }
}

async function cognitoRequest<T>(target: string, body: Record<string, unknown>): Promise<T> {
  const [region] = cognitoConfig.userPoolId.split("_");
  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(COGNITO_REQUEST_TIMEOUT_MS),
  });

  const responseBody = (await response.json()) as T & CognitoErrorBody;
  if (!response.ok) {
    const type = (responseBody.__type ?? "UnknownCognitoError").split("#").pop() ?? "Unknown";
    throw new CognitoSideChannelError(type, responseBody.message ?? "Cognito request failed");
  }

  return responseBody;
}

interface ChallengeResponse {
  ChallengeName?: string;
  Session?: string;
  AuthenticationResult?: { IdToken?: string };
}

/**
 * Starts the EMAIL_OTP challenge; the code is delivered to the user's email. The
 * username must be replayed verbatim in `verifyEmailOtpSideChannel`. An account that
 * can't take this challenge (password-only, FORCE_CHANGE_PASSWORD, …) answers with a
 * different challenge → thrown as `UnexpectedChallenge` so the modal steers to password.
 */
export async function requestEmailOtpSideChannel(username: string): Promise<{ session: string }> {
  const response = await cognitoRequest<ChallengeResponse>("InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: cognitoConfig.clientId,
    AuthParameters: {
      USERNAME: username,
      PREFERRED_CHALLENGE: EMAIL_OTP_CHALLENGE,
    },
  });

  if (response.ChallengeName !== EMAIL_OTP_CHALLENGE || !response.Session) {
    throw new CognitoSideChannelError(
      "UnexpectedChallenge",
      `Expected ${EMAIL_OTP_CHALLENGE} challenge, got ${response.ChallengeName ?? "none"}`
    );
  }

  return { session: response.Session };
}

/** Confirms the emailed OTP code and returns the freshly-minted ID token. */
export async function verifyEmailOtpSideChannel(params: {
  /** Must replay the exact USERNAME the InitiateAuth call used. */
  username: string;
  session: string;
  code: string;
}): Promise<{ freshIdToken: string }> {
  const response = await cognitoRequest<ChallengeResponse>("RespondToAuthChallenge", {
    ChallengeName: EMAIL_OTP_CHALLENGE,
    ClientId: cognitoConfig.clientId,
    Session: params.session,
    ChallengeResponses: {
      USERNAME: params.username,
      EMAIL_OTP_CODE: params.code,
    },
  });

  const freshIdToken = response.AuthenticationResult?.IdToken;
  if (!freshIdToken) {
    throw new CognitoSideChannelError(
      "NoAuthenticationResult",
      "OTP challenge did not return tokens"
    );
  }

  return { freshIdToken };
}

/**
 * Signs in with email + password over the raw wire (`InitiateAuth USER_PASSWORD_AUTH`) —
 * the minted tokens never enter Amplify storage, so the primary session is untouched and
 * a wrong password or cancel is harmless. If Cognito answers with a challenge (MFA,
 * NEW_PASSWORD_REQUIRED, …) instead of tokens, we don't drive it here — the modal steers
 * the user to the email-code fallback.
 */
export async function signInWithPasswordSideChannel(params: {
  email: string;
  password: string;
}): Promise<{ freshIdToken: string }> {
  const response = await cognitoRequest<ChallengeResponse>("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: cognitoConfig.clientId,
    AuthParameters: { USERNAME: params.email, PASSWORD: params.password },
  });

  const freshIdToken = response.AuthenticationResult?.IdToken;
  if (!freshIdToken) {
    throw new CognitoSideChannelError(
      "ChallengeRequired",
      `Password sign-in needs ${response.ChallengeName ?? "an additional challenge"} — use the emailed code instead`
    );
  }

  return { freshIdToken };
}
